import {merge, Box} from '@primer/react'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {testIdProps} from '@github-ui/test-id-props'
import type {Icon} from '@primer/octicons-react'
import {ActionBar as GitHubUiActionBar, type ActionBarProps as GitHubUiActionBarProps} from '@github-ui/action-bar'
import {useEffect, useRef} from 'react'
// eslint-disable-next-line no-restricted-imports
import {iterateFocusableElements} from '@primer/behaviors/utils'
import {useNestedListViewItems} from '../context/ItemsContext'

export type NestedListItemActionBarProps = Omit<GitHubUiActionBarProps, 'label' | 'variant'> & {
  /**
   * Override the icon used in the button that opens the action menu. Defaults to KebabHorizontalIcon.
   */
  anchorIcon?: Icon
  label?: GitHubUiActionBarProps['label']
  sx?: BetterSystemStyleObject
}
const defaultOuterContainerStyles: BetterSystemStyleObject = {
  display: 'flex',
  alignItems: 'center',
  paddingRight: 3,
  minWidth: '56px',
  gridArea: 'actions',
  maxWidth: '200px',
  flexGrow: 2,
}

export function NestedListItemActionBar({
  anchorIcon,
  sx: externalSx = {},
  label = 'list item action bar',
  ...props
}: NestedListItemActionBarProps) {
  const actionBarContainerRef = useRef(null)
  const {setAnyItemsWithActionBar, setHasResizableActionsWithActionBar} = useNestedListViewItems()
  useEffect(() => setAnyItemsWithActionBar(true), [setAnyItemsWithActionBar])
  useEffect(() => {
    if (props.actions) setHasResizableActionsWithActionBar(true)
  }, [props.actions, setHasResizableActionsWithActionBar])

  // Suppression of secondary info and actions
  // https://ui.githubapp.com/storybook/?path=/docs/recipes-nestedlistview-documentation-accessibility-listitem--docs#suppression-of-secondary-info-and-actions
  useEffect(() => {
    if (actionBarContainerRef.current) {
      const elements = [...iterateFocusableElements(actionBarContainerRef.current)]
      for (const el of elements) {
        el.tabIndex = -1
      }
    }
  }, [])

  const flexibleContainerSx = merge<BetterSystemStyleObject>(
    defaultOuterContainerStyles,
    props.actions
      ? {
          maxWidth: null,
        }
      : {},
  )
  const outerContainerSx = merge<BetterSystemStyleObject>(flexibleContainerSx, externalSx)

  return (
    <Box
      ref={actionBarContainerRef}
      aria-hidden="true"
      sx={outerContainerSx}
      {...testIdProps('nested-listview-item-action-bar-container')}
    >
      <GitHubUiActionBar
        {...props}
        label={label}
        variant="menu"
        overflowMenuToggleProps={anchorIcon ? {icon: anchorIcon} : undefined}
      />
    </Box>
  )
}

NestedListItemActionBar.displayName = 'NestedListItemActionBar'
